import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  CalendarClock,
  ChevronLeft,
  Edit2,
  History,
  Info,
  MessageSquareText,
  Package,
  Plus,
  RefreshCw,
  ShoppingBag,
  Trash2,
  Users,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MyInfoModal from './MyInfoModal';
import StoreInfoModal from './StoreInfoModal';
import NotFoundPage from './NotFoundPage';
import EditOrderBottomSheet from './EditOrderBottomSheet';
import {
  Order,
  OrderHistory as OrderHistoryLog,
  OrderItem,
  OrderSheet,
  Store,
  UnavailableOrderItem,
  UserProfile,
  WaitlistEntry,
} from '../types';
import { getOrderHistory } from '../api/history';
import { getStoreById } from '../api/stores';
import { getActiveOrderSheet } from '../api/order-sheets';
import { createOrder, getMyOrders, getOrder, updateOrder } from '../api/orders';
import { cancelWaitlist, getMyWaitlists } from '../api/waitlists';
import { usePickupPreference } from '../hooks/usePickupPreference';
import { useStoreRequestPreference } from '../hooks/useStoreRequestPreference';
import {
  formatStoreOperationSummary,
  buildPickupDateOptions,
  buildPickupHourOptions,
  getStorePickupHourExtraLabel,
} from '../utils/storeOperation';
import {
  buildStoredPickupLabel,
  buildStoreOrderRequestText,
  parseStoreOrderRequestText,
} from '../utils/storePickup';
import {
  getOrderItemDisplayName,
  getOrderItemOptionLabel,
} from '../utils/orderItemDisplay';

const formatUnavailableItemsMessage = (items: UnavailableOrderItem[]) =>
  items
    .map((item) => {
      const availabilityLabel =
        item.availableQuantity > 0
          ? `요청 ${item.requestedQuantity}개 / 가능 ${item.availableQuantity}개`
          : item.reason === 'OUT_OF_STOCK'
            ? `요청 ${item.requestedQuantity}개 / 품절`
            : `요청 ${item.requestedQuantity}개 / 주문 불가`;
      return `- ${item.name} (${availabilityLabel})`;
    })
    .join('\n');

interface CartPageProps {
  storeId: string;
  buyerProfile: UserProfile & { id?: string };
  setBuyerProfile: React.Dispatch<
    React.SetStateAction<UserProfile & { id?: string }>
  >;
  onBack: () => void;
  onLogout: () => void;
}

const STATUS_META: Record<
  string,
  { label: string; className: string }
> = {
  PENDING: {
    label: '주문 접수',
    className: 'bg-blue-50 text-blue-600 border border-blue-100',
  },
  READY: {
    label: '상품 준비중',
    className: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  COMPLETED: {
    label: '구매 완료',
    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  CANCELLED: {
    label: '취소됨',
    className: 'bg-red-50 text-red-500 border border-red-100',
  },
};

const shouldShowStatusBadge = (status?: string | null) =>
  Boolean(status && status !== 'CANCELLED');

const sanitizeImageUrl = (url?: string | null) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const getOrderItemAvailability = (item: OrderItem) => {
  const isUnavailable = Boolean(item.isProductDeleted);

  return {
    isUnavailable,
    label: item.availabilityLabel || '판매종료',
  };
};

const ACTION_LABEL: Record<string, string> = {
  UPDATE_ITEMS: '품목 수정',
  UPDATE_PRICE: '상품 가격 변경',
  UPDATE_STATUS: '상태 변경',
  UPDATE_INFO: '정보 변경',
  CREATE: '주문 생성',
  CANCEL: '주문 취소',
  BATCH_UPDATE: '일괄 상태 변경',
  PARTIAL_SPLIT_COMPLETE: '일부 품목 상태 변경',
  STOCK_RESTORED_BY_ORDER_CANCEL: '재고 복구 (취소)',
  STOCK_DEDUCTED_BY_ORDER: '재고 차감',
};

const getVisibleOrderItems = (items: OrderItem[]) =>
  items.filter((item) => item.quantity > 0);

const getVisibleOrderTotalPrice = (items: OrderItem[]) =>
  getVisibleOrderItems(items).reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

const buildOrderItemKey = (
  groupKey: string,
  item: OrderItem,
  index: number,
) => `${groupKey}-${item.id || `${item.productId}-${item.productOptionId || 'base'}-${index}`}`;

interface OrderSheetGroup {
  order: Order;
  sourceOrders: Order[];
  key: string;
  displayDate: string;
  orderSheetLabel: string;
  visibleItemCount: number;
  visibleTotalPrice: number;
  requestDisplay: {
    pickupLabel: string;
    requestMessage: string;
  };
}

type CombinedOrderContext = OrderSheetGroup;

const getOrderRequestDisplay = (request?: string) => {
  const rawRequest = request?.trim() || '';
  const { pickupLabel, requestMessage } = parseStoreOrderRequestText(rawRequest);

  return {
    pickupLabel,
    requestMessage: requestMessage || (!pickupLabel ? rawRequest : ''),
  };
};

const getOrderSheetGroupId = (order: Order) => order.orderSheetId || order.id;

const getOrderHistoryFamilyId = (order: Order) =>
  `${order.storeId}::${getOrderSheetGroupId(order)}`;

const getOrderSheetGroupKey = (order: Order, bucket: string) =>
  `${bucket}:${getOrderSheetGroupId(order)}`;

const getGroupedOrderStatus = (orders: Order[]): Order['status'] => {
  if (orders.some((order) => order.status === 'PENDING')) {
    return 'PENDING';
  }

  if (orders.some((order) => order.status === 'READY')) {
    return 'READY';
  }

  if (orders.some((order) => order.status === 'COMPLETED')) {
    return 'COMPLETED';
  }

  return 'CANCELLED';
};

const buildOrderSheetGroup = (
  key: string,
  orderList: Order[],
): OrderSheetGroup | null => {
  if (orderList.length === 0) {
    return null;
  }

  const sourceOrders = [...orderList].sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const latestOrder = sourceOrders[sourceOrders.length - 1];
  const itemMap = new Map<string, OrderItem>();

  sourceOrders.forEach((order) => {
    getVisibleOrderItems(order.items).forEach((item) => {
      const itemKey = `${item.productId || `CUSTOM:${item.name}`}::${item.productOptionId || ''}::${item.price}`;
      const existingItem = itemMap.get(itemKey);

      if (existingItem) {
        existingItem.quantity += item.quantity;
        return;
      }

      itemMap.set(itemKey, { ...item });
    });
  });

  const combinedItems = Array.from(itemMap.values());
  const visibleTotalPrice = getVisibleOrderTotalPrice(combinedItems);
  const requestSource = [...sourceOrders]
    .reverse()
    .find((order) => order.request?.trim());
  const request = requestSource?.request || latestOrder.request;

  return {
    key,
    sourceOrders,
    displayDate: latestOrder.date,
    orderSheetLabel:
      latestOrder.orderSheet?.label
      || sourceOrders[0].orderSheet?.label
      || '미지정 주문지',
    visibleItemCount: combinedItems.length,
    visibleTotalPrice,
    requestDisplay: getOrderRequestDisplay(request),
    order: {
      ...latestOrder,
      items: combinedItems,
      totalPrice: visibleTotalPrice,
      request,
      status: getGroupedOrderStatus(sourceOrders),
    },
  };
};

const buildOrderSheetGroups = (
  orderList: Order[],
  bucket: string,
): OrderSheetGroup[] => {
  const groupedOrders = new Map<string, Order[]>();

  orderList.forEach((order) => {
    const key = getOrderSheetGroupKey(order, bucket);
    const current = groupedOrders.get(key) || [];
    current.push(order);
    groupedOrders.set(key, current);
  });

  return Array.from(groupedOrders.entries())
    .map(([key, grouped]) => buildOrderSheetGroup(key, grouped))
    .filter((group): group is OrderSheetGroup => Boolean(group))
    .sort(
      (a, b) =>
        new Date(b.order.createdAt).getTime()
        - new Date(a.order.createdAt).getTime(),
    );
};

const replaceOrdersById = (currentOrders: Order[], updatedOrders: Order[]) => {
  const updatedMap = new Map(updatedOrders.map((order) => [order.id, order]));
  const mergedOrders = currentOrders.map(
    (order) => updatedMap.get(order.id) || order,
  );

  updatedOrders.forEach((order) => {
    if (!mergedOrders.some((existingOrder) => existingOrder.id === order.id)) {
      mergedOrders.push(order);
    }
  });

  return mergedOrders;
};

const HistoryStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (!shouldShowStatusBadge(status)) {
    return null;
  }

  const meta = STATUS_META[status] || {
    label: status,
    className: 'bg-gray-100 text-gray-500 border border-gray-200',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-[10px] font-black ${meta.className}`}>
      {meta.label}
    </span>
  );
};

const OrderItemCard: React.FC<{
  groupKey: string;
  item: OrderItem;
  index: number;
}> = ({ groupKey, item, index }) => {
  const availability = getOrderItemAvailability(item);
  const imageUrl = sanitizeImageUrl(item.image);
  const displayName = getOrderItemDisplayName(item);
  const optionLabel = getOrderItemOptionLabel(item);
  const totalItemPrice = item.price * item.quantity;
  const isDimmed = availability.isUnavailable;

  return (
    <div
      key={buildOrderItemKey(groupKey, item, index)}
      className={`rounded-[24px] border p-3 shadow-sm ${isDimmed
          ? 'bg-gray-50 border-dashed border-gray-200 opacity-70'
          : 'bg-white border-emerald-100'
        }`}
    >
      <div className="flex items-center gap-3">
        {imageUrl ? (
          <img
            src={imageUrl}
            className={`w-20 h-20 rounded-[20px] object-cover border bg-gray-50 ${isDimmed ? 'grayscale' : ''
              }`}
            alt={displayName}
          />
        ) : (
          <div className="w-20 h-20 rounded-[20px] border bg-gray-50 flex items-center justify-center text-gray-300">
            <Package size={24} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p
                className={`text-[15px] font-black leading-tight break-words ${isDimmed ? 'text-gray-500' : 'text-gray-900'
                  }`}
              >
                {displayName}
              </p>
              {optionLabel && (
                <p
                  className={`mt-1 text-[11px] font-black ${isDimmed ? 'text-gray-400' : 'text-emerald-600'
                    }`}
                >
                  옵션: {optionLabel}
                </p>
              )}
              <p
                className={`mt-2 text-[18px] font-black ${isDimmed ? 'text-gray-500' : 'text-emerald-600'
                  }`}
              >
                {totalItemPrice.toLocaleString()}원
              </p>
              <p className="mt-1 text-[11px] font-bold text-gray-400">
                수량 {item.quantity}개 · 개당 {item.price.toLocaleString()}원
              </p>
            </div>
            {availability.isUnavailable && (
              <span className="shrink-0 rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-black text-white">
                {availability.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const describeItemChange = (change: {
  name: string;
  oldQty: number;
  newQty: number;
  diff: number;
  changeKind?: string;
}) => {
  if (change.changeKind === 'STATUS_ONLY' || change.diff === 0) {
    return {
      text: `${change.name} ${change.newQty}개 상태 변경`,
      type: 'status' as const,
    };
  }
  if (change.newQty === 0) {
    return { text: `${change.name} 취소`, type: 'remove' as const };
  }
  if (change.oldQty === 0) {
    return { text: `${change.name} ${change.newQty}개 추가`, type: 'add' as const };
  }
  return {
    text: `${change.name} ${change.oldQty}개 → ${change.newQty}개`,
    type: change.diff > 0 ? ('increase' as const) : ('decrease' as const),
  };
};

const OrderEditTimeline: React.FC<{ logs: OrderHistoryLog[] }> = ({ logs }) => {
  if (logs.length === 0) {
    return (
      <div className="mt-3 rounded-2xl bg-gray-50 px-4 py-3 text-center">
        <p className="text-[11px] font-bold text-gray-400">변경 이력이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-[20px] border border-gray-100 bg-gray-50/70 px-4 py-4">
      <div className="relative">
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gray-200" />
        <div className="space-y-4">
          {logs.map((log, index) => {
            const hasItemChanges = (log.details?.itemChanges?.length || 0) > 0;
            const priceChanges = Array.isArray(log.details?.priceChanges)
              ? log.details.priceChanges
              : [];
            const hasPriceChange =
              log.details?.oldPrice !== undefined && log.details?.newPrice !== undefined;
            const isStatusChange = log.oldStatus && log.oldStatus !== log.newStatus;
            const isFirst = index === logs.length - 1;
            const isBuyer = log.modifierRole === 'CUSTOMER' || log.modifierRole === 'USER';

            return (
              <div key={log.id} className="flex gap-3">
                <div
                  className={`z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${isFirst
                      ? 'bg-emerald-500 text-white'
                      : isStatusChange
                        ? 'border-2 border-amber-400 bg-amber-100 text-amber-600'
                        : 'border-2 border-gray-300 bg-white text-gray-400'
                    }`}
                >
                  {isFirst ? (
                    <Package size={11} strokeWidth={2.5} />
                  ) : isStatusChange ? (
                    <ArrowRight size={10} strokeWidth={3} />
                  ) : (
                    <RefreshCw size={10} strokeWidth={2.5} />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-[10px] font-black ${isBuyer
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                          }`}
                      >
                        {isBuyer ? '본인' : '판매자'}
                      </span>
                      <span className="text-[11px] font-black text-gray-800">
                        {ACTION_LABEL[log.action || ''] || log.action || '변경'}
                      </span>
                    </div>
                    <span className="shrink-0 text-[10px] font-bold text-gray-400">
                      {new Date(log.createdAt).toLocaleString('ko-KR', {
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  {isStatusChange
                    && shouldShowStatusBadge(log.oldStatus)
                    && shouldShowStatusBadge(log.newStatus) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <HistoryStatusBadge status={log.oldStatus!} />
                        <ArrowRight size={12} className="text-gray-300" />
                        <HistoryStatusBadge status={log.newStatus} />
                      </div>
                    )}

                {hasItemChanges && (
                    <div className="mt-2 rounded-xl border border-gray-100 bg-white px-3 py-2">
                      {log.details.itemChanges.map(
                        (
                          change: {
                            name: string;
                            oldQty: number;
                            newQty: number;
                            diff: number;
                          },
                          changeIndex: number,
                        ) => {
                          const { text, type } = describeItemChange(change);
                          return (
                            <div
                              key={`${log.id}-${changeIndex}`}
                              className="flex items-center gap-2 py-0.5"
                            >
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${type === 'remove'
                                    ? 'bg-red-100 text-red-500'
                                    : type === 'add'
                                      ? 'bg-green-100 text-green-600'
                                      : type === 'status'
                                        ? 'bg-slate-100 text-slate-500'
                                        : type === 'increase'
                                          ? 'bg-blue-100 text-blue-500'
                                          : 'bg-orange-100 text-orange-500'
                                  }`}
                              >
                                {type === 'remove' ? (
                                  <Trash2 size={10} strokeWidth={3} />
                                ) : type === 'add' ? (
                                  <Plus size={10} strokeWidth={3} />
                                ) : (
                                  <RefreshCw size={10} strokeWidth={3} />
                )}

                {priceChanges.length > 0 && (
                  <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 space-y-1 shadow-sm">
                    {priceChanges.map((change: any, idx: number) => (
                      <div key={idx} className="text-[10px] font-bold text-gray-500">
                        <span className="text-gray-800">{change.name}</span>{' '}
                        {change.oldUnitPrice.toLocaleString()}원 →{' '}
                        <span className="text-emerald-700">
                          {change.newUnitPrice.toLocaleString()}원
                        </span>{' '}
                        · {change.quantity}개
                      </div>
                    ))}
                  </div>
                )}
                              </span>
                              <span
                                className={`text-[11px] font-bold ${type === 'remove'
                                    ? 'text-red-500'
                                    : type === 'add'
                                      ? 'text-green-600'
                                      : type === 'status'
                                        ? 'text-slate-500'
                                        : type === 'increase'
                                          ? 'text-blue-600'
                                          : 'text-orange-500'
                                  }`}
                              >
                                {text}
                              </span>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}

                  {hasPriceChange && (
                    <div className="mt-2 flex w-fit items-center gap-1.5 rounded-lg border border-gray-100 bg-white px-2.5 py-1.5 text-[10px] font-bold text-gray-500">
                      <span>{log.details.oldPrice.toLocaleString()}원</span>
                      <ArrowRight size={10} className="text-gray-300" />
                      <span className="text-gray-800">
                        {log.details.newPrice.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CartPage: React.FC<CartPageProps> = ({
  storeId,
  buyerProfile,
  setBuyerProfile,
  onBack,
  onLogout,
}) => {
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const resolvedStoreId = currentStore?.id || storeId;
  const [activeOrderSheet, setActiveOrderSheet] = useState<OrderSheet | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [waitlists, setWaitlists] = useState<WaitlistEntry[]>([]);
  const [cancellingWaitlistId, setCancellingWaitlistId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [isMyInfoOpen, setIsMyInfoOpen] = useState(false);
  const [isStoreInfoOpen, setIsStoreInfoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'CART' | 'HISTORY'>('CART');
  const [editingOrderContext, setEditingOrderContext] =
    useState<CombinedOrderContext | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<Record<string, OrderHistoryLog[]>>({});
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});

  // Pickup editing states
  const [isEditingPickup, setIsEditingPickup] = useState(false);
  const [selectedPickupDate, setSelectedPickupDate] = useState('');
  const [selectedPickupHour, setSelectedPickupHour] = useState<number | ''>('');
  const [isSavingPickupState, setIsSavingPickupState] = useState(false);
  const [isEditingRequestMessage, setIsEditingRequestMessage] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestMessageDraft, setRequestMessageDraft] = useState('');
  const [isSavingRequestMessage, setIsSavingRequestMessage] = useState(false);
  const acceptsPickupTime = currentStore
    ? currentStore.acceptsPickupTime !== false
    : true;
  const acceptsRequestMessage = currentStore
    ? currentStore.acceptsRequestMessage !== false
    : true;
  const canCustomerEditOrder = currentStore
    ? currentStore.acceptsOrderEdit !== false
    : false;

  const { pickupSelection: savedPickup, savePickupSelection } = usePickupPreference({
    enabled: Boolean(currentStore) && acceptsPickupTime,
    storeId: resolvedStoreId,
    userId: buyerProfile.id,
  });
  const {
    requestMessage: savedRequestMessage,
    saveRequestMessage,
  } = useStoreRequestPreference({
    enabled: Boolean(currentStore) && acceptsRequestMessage,
    storeId: resolvedStoreId,
    userId: buyerProfile.id,
  });

  const pickupDateOptions = useMemo(() => {
    return buildPickupDateOptions(
      currentStore?.operationDayOfWeeks,
      currentStore?.operationStartHour,
      currentStore?.operationEndHour,
    );
  }, [
    currentStore?.operationDayOfWeeks,
    currentStore?.operationStartHour,
    currentStore?.operationEndHour,
  ]);

  const pickupHourOptions = useMemo(() => {
    return buildPickupHourOptions(
      currentStore?.operationStartHour,
      currentStore?.operationEndHour,
      selectedPickupDate,
      new Date(),
      currentStore?.pickupUnmannedStartHour,
      currentStore?.pickupUnmannedLabel,
    );
  }, [
    currentStore?.operationStartHour,
    currentStore?.operationEndHour,
    currentStore?.pickupUnmannedStartHour,
    currentStore?.pickupUnmannedLabel,
    selectedPickupDate,
  ]);

  useEffect(() => {
    if (!acceptsPickupTime) {
      setIsEditingPickup(false);
      return;
    }

    if (savedPickup) {
      setSelectedPickupDate(savedPickup.dateValue);
      setSelectedPickupHour(savedPickup.hour);
    } else if (pickupDateOptions.length > 0) {
      setSelectedPickupDate(pickupDateOptions[0].value);
    }
  }, [acceptsPickupTime, savedPickup, pickupDateOptions]);

  useEffect(() => {
    if (!acceptsRequestMessage) {
      setIsEditingRequestMessage(false);
      setRequestMessage('');
      setRequestMessageDraft('');
    }
  }, [acceptsRequestMessage]);

  const handleSavePickup = async () => {
    if (!acceptsPickupTime) {
      return;
    }

    const resolvedPickupDate = selectedPickupDate || pickupDateOptions[0]?.value || '';

    if (!resolvedPickupDate) {
      alert('현재 선택 가능한 픽업 일정이 없습니다.');
      return;
    }

    if (selectedPickupHour === '') {
      alert('픽업 시간을 선택해주세요.');
      return;
    }

    setIsSavingPickupState(true);
    const hour = Number(selectedPickupHour);
    const label = buildStoredPickupLabel(
      resolvedPickupDate,
      hour,
      getStorePickupHourExtraLabel(currentStore, hour),
    );

    try {
      const savedSelection = await savePickupSelection({
        dateValue: resolvedPickupDate,
        hour,
        label,
        updatedAt: new Date().toISOString(),
      });
      setSelectedPickupDate(resolvedPickupDate);
      await syncPendingOrdersRequest(savedSelection.label, requestMessage);
      setIsEditingPickup(false);
    } catch (error) {
      console.error('Failed to save pickup preference', error);
      alert('픽업 일정 저장에 실패했습니다.');
    } finally {
      setIsSavingPickupState(false);
    }
  };

  useEffect(() => {
    if (!storeId) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setStoreError(null);
      try {
        const store = await getStoreById(storeId).catch(() => null);
        if (!store) {
          setStoreError('STORE_NOT_FOUND');
          return;
        }

        if (store.orderType !== 'STORE') {
          setStoreError('NOT_STORE_ORDER');
          return;
        }

        const [nextActiveOrderSheet, myOrders, myWaitlists] = await Promise.all([
          getActiveOrderSheet(store.id).catch(() => null),
          buyerProfile.id || buyerProfile.name || buyerProfile.phone
            ? getMyOrders(
              buyerProfile.name,
              buyerProfile.phone,
              buyerProfile.id,
            )
            : Promise.resolve([]),
          getMyWaitlists({ storeId: store.id }),
        ]);

        setCurrentStore(store);
        setActiveOrderSheet(nextActiveOrderSheet);
        setOrders(myOrders);
        setWaitlists(myWaitlists);
      } catch (error) {
        console.error('Failed to fetch store cart page data', error);
        setStoreError('FETCH_ERROR');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    buyerProfile.id,
    buyerProfile.name,
    buyerProfile.phone,
    storeId,
  ]);

  useEffect(() => {
    if (currentStore) {
      document.title = `${currentStore.name} 주문내역`;
    }
  }, [currentStore]);

  const storeOrders = useMemo(
    () => orders.filter((order) => order.storeId === resolvedStoreId),
    [orders, resolvedStoreId],
  );

  const pendingOrders = useMemo(
    () =>
      storeOrders.filter(
        (order) =>
          order.status !== 'COMPLETED'
          && order.status !== 'CANCELLED'
          && getVisibleOrderItems(order.items).length > 0,
      ),
    [storeOrders],
  );

  const storeWaitlists = useMemo(
    () => waitlists.filter((entry) => entry.storeId === resolvedStoreId),
    [resolvedStoreId, waitlists],
  );

  const handleCancelWaitlist = useCallback(async (entry: WaitlistEntry) => {
    const productLabel = entry.optionName
      ? `${entry.productName || '상품'} (${entry.optionName})`
      : entry.productName || '상품';

    if (
      !window.confirm(
        `${productLabel}의 남은 ${entry.remainingQuantity}개 줄서기를 취소하시겠습니까? 이미 주문에 반영된 수량은 유지됩니다.`,
      )
    ) {
      return;
    }

    setCancellingWaitlistId(entry.id);
    try {
      await cancelWaitlist(entry.id);
      setWaitlists((current) => current.filter((item) => item.id !== entry.id));
      alert('줄서기가 취소되었습니다.');
    } catch (error: any) {
      console.error('Failed to cancel waitlist', error);
      alert(
        error?.response?.data?.message
        || '줄서기를 취소하지 못했습니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setCancellingWaitlistId(null);
    }
  }, []);

  const completedOrders = useMemo(
    () =>
      storeOrders.filter(
        (order) =>
          order.status === 'COMPLETED' && getVisibleOrderItems(order.items).length > 0,
      ),
    [storeOrders],
  );

  const editablePendingOrders = useMemo(
    () =>
      pendingOrders.filter((order) => {
        if (activeOrderSheet?.id) {
          return order.orderSheetId === activeOrderSheet.id;
        }

        return !order.orderSheetId;
      }),
    [activeOrderSheet?.id, pendingOrders],
  );

  const pendingItemCount = useMemo(
    () =>
      pendingOrders.reduce(
        (sum, order) =>
          sum
          + getVisibleOrderItems(order.items).reduce(
            (acc, item) => acc + item.quantity,
            0,
          ),
        0,
      ),
    [pendingOrders],
  );

  const completedItemCount = useMemo(
    () =>
      completedOrders.reduce(
        (sum, order) =>
          sum
          + getVisibleOrderItems(order.items).reduce(
            (acc, item) => acc + item.quantity,
            0,
          ),
        0,
      ),
    [completedOrders],
  );

  const pendingOrderSheetGroups = useMemo(
    () => buildOrderSheetGroups(pendingOrders, 'pending'),
    [pendingOrders],
  );

  const completedOrderSheetGroups = useMemo(
    () => buildOrderSheetGroups(completedOrders, 'completed'),
    [completedOrders],
  );

  const editableGroupKey = useMemo(
    () =>
      editablePendingOrders.length > 0
        ? getOrderSheetGroupKey(editablePendingOrders[0], 'pending')
        : null,
    [editablePendingOrders],
  );

  const pendingEditContext = useMemo(
    () =>
      editableGroupKey
        ? buildOrderSheetGroup(editableGroupKey, editablePendingOrders)
        : null,
    [editableGroupKey, editablePendingOrders],
  );

  const hasUnavailablePendingItems = useMemo(
    () =>
      pendingOrders.some((order) =>
        getVisibleOrderItems(order.items).some((item) => item.isProductDeleted),
      ),
    [pendingOrders],
  );

  const isOrderEditable = (order: Order) =>
    !['COMPLETED', 'CANCELLED'].includes(order.status)
    && order.orderSheet?.isClosed !== true;

  const fallbackRequestMessage = useMemo(() => {
    if (!acceptsRequestMessage) {
      return '';
    }

    const orderWithRequest = editablePendingOrders.find((order) =>
      parseStoreOrderRequestText(order.request).requestMessage.trim(),
    );
    if (!orderWithRequest) return '';
    return parseStoreOrderRequestText(orderWithRequest.request).requestMessage;
  }, [acceptsRequestMessage, editablePendingOrders]);

  useEffect(() => {
    const nextMessage = acceptsRequestMessage
      ? savedRequestMessage || fallbackRequestMessage
      : '';
    setRequestMessage(nextMessage);
    setRequestMessageDraft(nextMessage);
  }, [acceptsRequestMessage, fallbackRequestMessage, savedRequestMessage]);

  const syncPendingOrdersRequest = async (
    pickupLabel: string | null | undefined,
    nextRequestMessage: string,
  ) => {
    const currentEditableOrders = editablePendingOrders.filter(isOrderEditable);
    if (currentEditableOrders.length === 0) {
      return;
    }

    const nextRequest = buildStoreOrderRequestText(
      acceptsPickupTime ? pickupLabel : null,
      acceptsRequestMessage ? nextRequestMessage : '',
    );
    const updatedOrders = await Promise.all(
      currentEditableOrders.map((order) =>
        updateOrder(order.id, { request: nextRequest }),
      ),
    );
    const updatedMap = new Map(updatedOrders.map((order) => [order.id, order]));

    setOrders((prev) =>
      prev.map((order) => updatedMap.get(order.id) || order),
    );
  };

  const reloadEditingOrderContext = useCallback(async () => {
    if (!editingOrderContext) {
      return null;
    }

    const latestOrders = await Promise.all(
      editingOrderContext.sourceOrders.map((order) => getOrder(order.id)),
    );

    setOrders((prev) => replaceOrdersById(prev, latestOrders));

    const nextGroup = buildOrderSheetGroup(editingOrderContext.key, latestOrders);
    if (!nextGroup) {
      setEditingOrderContext(null);
      return null;
    }

    setEditingOrderContext(nextGroup);
    setExpandedHistory((prev) => {
      const next = { ...prev };
      delete next[editingOrderContext.key];
      return next;
    });
    setLoadingHistory((prev) => {
      const next = { ...prev };
      delete next[editingOrderContext.key];
      return next;
    });

    return nextGroup.order;
  }, [editingOrderContext]);

  const handleEditOrderSave = async (items: OrderItem[]) => {
    if (!editingOrderContext || !currentStore) {
      return;
    }

    try {
      const visibleItems = items.filter((item) => item.quantity > 0);
      const sourceOrderIds = editingOrderContext.sourceOrders.map((order) => order.id);

      if (visibleItems.length === 0) {
        await Promise.all(
          sourceOrderIds.map((orderId) =>
            updateOrder(orderId, { status: 'CANCELLED' }),
          ),
        );

        const [refreshedOrders, refreshedWaitlists] = await Promise.all([
          buyerProfile.id || buyerProfile.name || buyerProfile.phone
            ? getMyOrders(
              buyerProfile.name,
              buyerProfile.phone,
              buyerProfile.id,
            )
            : Promise.resolve([]),
          getMyWaitlists({ storeId: currentStore.id }),
        ]);

        setOrders(refreshedOrders);
        setWaitlists(refreshedWaitlists);
        setExpandedHistory((prev) => {
          const next = { ...prev };
          delete next[editingOrderContext.key];
          return next;
        });
        setLoadingHistory((prev) => {
          const next = { ...prev };
          delete next[editingOrderContext.key];
          return next;
        });
        setEditingOrderContext(null);
        alert('주문이 취소되었습니다.');
        return;
      }

      const totalPrice = visibleItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );
      const preservedPickupLabel =
        acceptsPickupTime
          ? savedPickup?.label
          || parseStoreOrderRequestText(editingOrderContext.order.request).pickupLabel
          || null
          : null;
      let successAlertMessage = '주문이 수정되었습니다.';

      const { order, unavailableItems, resultType } = await createOrder({
        ...(activeOrderSheet?.id || editingOrderContext.order.orderSheetId
          ? {
            orderSheetId:
              activeOrderSheet?.id || editingOrderContext.order.orderSheetId,
          }
          : {}),
        storeId: currentStore.id,
        storeName: currentStore.name,
        items: visibleItems.map((item) => ({
          productId: item.productId,
          productOptionId: item.productOptionId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
        totalPrice,
        customerName: buyerProfile.name.trim() || editingOrderContext.order.customerName,
        customerPhone:
          buyerProfile.phone.trim()
          || editingOrderContext.order.customerPhone
          || '',
        request: buildStoreOrderRequestText(
          preservedPickupLabel,
          acceptsRequestMessage ? requestMessage : '',
        ),
        userId: buyerProfile.id,
      });

      if (!order) {
        alert(
          `주문 가능한 상품이 없어 수정되지 않았습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
        );
        return;
      }

      if (resultType === 'UNCHANGED_WITH_UNAVAILABLE') {
        successAlertMessage = `재고 부족 또는 주문 불가 상품은 반영되지 않아 주문 상품이 그대로 유지되었습니다.\n\n반영되지 않은 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`;
      } else if (unavailableItems.length > 0) {
        successAlertMessage = `일부 상품은 품절 또는 주문 불가로 제외되고 나머지만 수정되었습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`;
      }

      const [refreshedOrders, refreshedWaitlists] = await Promise.all([
        buyerProfile.id || buyerProfile.name || buyerProfile.phone
          ? getMyOrders(
            buyerProfile.name,
            buyerProfile.phone,
            buyerProfile.id,
          )
          : Promise.resolve([]),
        getMyWaitlists({ storeId: currentStore.id }),
      ]);

      setOrders(refreshedOrders);
      setWaitlists(refreshedWaitlists);
      setExpandedHistory((prev) => {
        const next = { ...prev };
        delete next[editingOrderContext.key];
        return next;
      });
      setLoadingHistory((prev) => {
        const next = { ...prev };
        delete next[editingOrderContext.key];
        return next;
      });
      setEditingOrderContext(null);
      alert(successAlertMessage);
    } catch (error: any) {
      console.error('Failed to save order edit:', error);
      const message = error?.response?.data?.message || '주문 수정에 실패했습니다.';
      alert(message);
    }
  };

  const handleFetchHistory = async (group: OrderSheetGroup) => {
    if (expandedHistory[group.key]) {
      const next = { ...expandedHistory };
      delete next[group.key];
      setExpandedHistory(next);
      return;
    }

    setLoadingHistory((prev) => ({ ...prev, [group.key]: true }));

    try {
      const relatedOrders = Array.from(
        new Map<string, Order>(
          storeOrders
            .filter(
              (order) =>
                getOrderHistoryFamilyId(order)
                === getOrderHistoryFamilyId(group.order),
            )
            .map((order) => [order.id, order]),
        ).values(),
      );
      const historyResponses = await Promise.all(
        relatedOrders.map((order) => getOrderHistory(order.id)),
      );
      const history = historyResponses
        .flat()
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime()
            - new Date(a.createdAt).getTime(),
        );
      setExpandedHistory((prev) => ({ ...prev, [group.key]: history }));
    } catch (error) {
      console.error('Failed to fetch order history', error);
      alert('주문 이력을 불러오지 못했습니다.');
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [group.key]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (storeError === 'STORE_NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (storeError === 'NOT_STORE_ORDER') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-10 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 mb-6">
          <Package size={40} />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">
          매장형 주문 주문내역이 아닙니다
        </h3>
        <p className="text-gray-500 font-bold leading-relaxed mb-8">
          배송형 주문은 기존 주문내역에서 확인할 수 있습니다.
        </p>
        <button
          onClick={onBack}
          className="px-8 h-14 bg-gray-900 text-white font-bold rounded-2xl"
        >
          주문 페이지로 돌아가기
        </button>
      </div>
    );
  }

  if (storeError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-10 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-6">
          <Info size={40} />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">
          주문내역을 불러오지 못했습니다
        </h3>
        <p className="text-gray-500 font-bold leading-relaxed mb-8">
          잠시 후 다시 시도해주세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-8 h-14 bg-gray-900 text-white font-bold rounded-2xl"
        >
          새로고침
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col max-w-lg mx-auto bg-white shadow-xl relative pb-24">
      <Header
        storeName={currentStore?.name || '주문내역'}
        onLogout={onLogout}
        onStoreInfoClick={() => setIsStoreInfoOpen(true)}
        onMyInfoClick={() => setIsMyInfoOpen(true)}
      />

      <main className="flex-1">
        <div className="px-6 py-4 border-b bg-gray-50/50">
          <div className="bg-white border border-gray-100 rounded-[24px] p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-gray-900 mt-1">
                  주문내역
                </h2>
              </div>
              <button
                onClick={onBack}
                className="shrink-0 h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-[12px] font-black flex items-center gap-1.5"
              >
                <ChevronLeft size={14} />
                더 담기
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <div
                className="rounded-xl bg-blue-50/50 px-3 py-2 border border-blue-100/50"
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black text-blue-700">주문내역</p>
                  <p className="text-[14px] font-black text-gray-900">
                    {pendingOrderSheetGroups.length}건
                  </p>
                </div>
                <p className="text-[10px] font-bold text-blue-600 mt-0.5">
                  {pendingItemCount}개 상품 진행 중
                </p>
              </div>
              <div
                className="rounded-xl bg-emerald-50/50 px-3 py-2 border border-emerald-100/50"
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black text-emerald-700">픽업 완료</p>
                  <p className="text-[14px] font-black text-gray-900">
                    {completedOrderSheetGroups.length}건
                  </p>
                </div>
                <p className="text-[10px] font-bold text-emerald-600 mt-0.5">
                  {completedItemCount}개 상품 완료
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-[11px] font-bold text-gray-400">
                운영 기준:{' '}
                <span className="text-emerald-600">
                  {currentStore ? formatStoreOperationSummary(currentStore) : ''}
                </span>
              </p>
              {acceptsPickupTime && (isEditingPickup ? (
                <div className="mt-2 p-4 bg-gray-50 rounded-2xl border border-emerald-100 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CalendarClock size={14} className="text-emerald-600" />
                    <span className="text-[11px] font-black text-emerald-700 uppercase">픽업 일정 수정</span>
                  </div>
                  <div className="space-y-2">
                    <select
                      value={selectedPickupHour === '' ? '' : selectedPickupHour}
                      onChange={(e) => setSelectedPickupHour(Number(e.target.value))}
                      className="h-10 px-3 rounded-xl border border-emerald-200 bg-white text-[13px] font-bold outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    >
                      {pickupHourOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] font-bold text-emerald-700">
                      날짜는 가장 빠른 가능 일정으로 자동 적용됩니다.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditingPickup(false)}
                      className="flex-1 h-10 rounded-xl bg-white border border-gray-200 text-[13px] font-bold text-gray-500"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSavePickup}
                      disabled={isSavingPickupState}
                      className="flex-[2] h-10 rounded-xl bg-emerald-500 text-white text-[13px] font-black flex items-center justify-center gap-2 disabled:bg-emerald-300"
                    >
                      {isSavingPickupState && <RefreshCw size={14} className="animate-spin" />}
                      픽업 일정 수정완료
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <p className="text-[11px] font-bold text-gray-400">
                    현재 저장된 픽업 일정:{' '}
                    <span className="text-gray-700 font-black">{savedPickup?.label || '미지정'}</span>
                  </p>
                  <button
                    onClick={() => setIsEditingPickup(true)}
                    className="shrink-0 h-7 px-2.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-[10px] font-black hover:bg-gray-50 active:scale-95 transition-all"
                  >
                    수정
                  </button>
                </div>
              ))}
              {currentStore?.deliverySchedule && (
                <p className="text-[11px] font-bold text-gray-400">
                  판매자 메시지:{' '}
                  <span className="text-gray-700">
                    {currentStore.deliverySchedule}
                  </span>
                </p>
              )}
              {acceptsRequestMessage && (isEditingRequestMessage ? (
                <div className="mt-2 p-4 bg-sky-50/70 rounded-2xl border border-sky-100 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquareText size={14} className="text-sky-600" />
                    <span className="text-[11px] font-black text-sky-700 uppercase">요청 메시지 수정</span>
                  </div>
                  <textarea
                    value={requestMessageDraft}
                    onChange={(e) => setRequestMessageDraft(e.target.value)}
                    rows={4}
                    placeholder="사장님께 전달할 요청 메시지를 입력해주세요."
                    className="w-full rounded-2xl border border-sky-200 bg-white px-4 py-3 text-[13px] font-bold text-gray-700 outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setRequestMessageDraft(requestMessage);
                        setIsEditingRequestMessage(false);
                      }}
                      className="flex-1 h-10 rounded-xl bg-white border border-gray-200 text-[13px] font-bold text-gray-500"
                    >
                      취소
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          setIsSavingRequestMessage(true);
                          const trimmedRequestMessage = saveRequestMessage(
                            requestMessageDraft,
                          );
                          setRequestMessage(trimmedRequestMessage);
                          setRequestMessageDraft(trimmedRequestMessage);
                          await syncPendingOrdersRequest(
                            savedPickup?.label,
                            trimmedRequestMessage,
                          );
                          setIsEditingRequestMessage(false);
                        } catch (error) {
                          console.error('Failed to save request message', error);
                          alert('요청 메시지 저장에 실패했습니다.');
                        } finally {
                          setIsSavingRequestMessage(false);
                        }
                      }}
                      disabled={isSavingRequestMessage}
                      className="flex-[2] h-10 rounded-xl bg-sky-500 text-white text-[13px] font-black flex items-center justify-center gap-2 disabled:bg-sky-300"
                    >
                      {isSavingRequestMessage && (
                        <RefreshCw size={14} className="animate-spin" />
                      )}
                      요청 메시지 저장
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <p className="text-[11px] font-bold text-gray-400 whitespace-pre-line">
                    현재 요청 메시지:{' '}
                    <span className="text-gray-700 font-black">
                      {requestMessage.trim() || '미입력'}
                    </span>
                  </p>
                  <button
                    onClick={() => setIsEditingRequestMessage(true)}
                    className="shrink-0 h-7 px-2.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-[10px] font-black hover:bg-gray-50 active:scale-95 transition-all"
                  >
                    수정
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 px-6">
            <div className="bg-gray-100 p-1.5 rounded-[20px] flex gap-1">
              <button
                onClick={() => setActiveTab('CART')}
                className={`flex-1 h-11 rounded-[16px] text-sm font-black transition-all ${activeTab === 'CART'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                주문내역
              </button>
              <button
                onClick={() => setActiveTab('HISTORY')}
                className={`flex-1 h-11 rounded-[16px] text-sm font-black transition-all ${activeTab === 'HISTORY'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                구매내역
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'CART' && (
          <section className="px-6 py-5 space-y-3 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-black text-gray-900">주문내역</h3>
              <p className="text-[11px] font-bold text-gray-400 mt-1">
                아직 수령완료 처리되지 않은 주문과 줄서기 내역입니다.
              </p>
              {hasUnavailablePendingItems && (
                <p className="text-[11px] font-black text-amber-700 mt-2">
                  회색으로 표시된 항목은 판매 종료된 상품입니다.
                </p>
              )}
            </div>

            {storeWaitlists.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 pt-1">
                  <Users size={15} className="text-amber-600" />
                  <h4 className="text-[12px] font-black text-gray-800">줄서기 내역</h4>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700">
                    {storeWaitlists.reduce(
                      (sum, entry) => sum + entry.remainingQuantity,
                      0,
                    )}개
                  </span>
                </div>
                {storeWaitlists.map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-[24px] border border-amber-200 bg-amber-50/60 p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-[15px] font-black leading-tight text-gray-900">
                          {entry.productName || '상품 정보 확인 중'}
                        </p>
                        {entry.optionName && (
                          <p className="mt-1 text-[11px] font-black text-amber-700">
                            옵션: {entry.optionName}
                          </p>
                        )}
                        <p className="mt-2 text-[11px] font-bold text-gray-400">
                          {new Date(entry.createdAt).toLocaleDateString('ko-KR', {
                            month: 'long',
                            day: 'numeric',
                          })}{' '}
                          신청 {entry.quantity}개
                        </p>
                        {entry.fulfilledQuantity > 0 && (
                          <p className="mt-1 text-[11px] font-black text-emerald-600">
                            주문 반영 {entry.fulfilledQuantity}개
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 rounded-2xl bg-white px-3 py-2 text-center shadow-sm ring-1 ring-amber-200">
                        <span className="block text-[10px] font-black text-gray-400">
                          남은 수량
                        </span>
                        <span className="mt-0.5 block text-sm font-black text-amber-700">
                          {entry.remainingQuantity}개
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-[10px] font-bold text-amber-700">
                      재고가 생기면 순서대로 주문에 자동 반영됩니다.
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleCancelWaitlist(entry)}
                      disabled={cancellingWaitlistId !== null}
                      className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-red-100 bg-white text-[11px] font-black text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 size={13} />
                      {cancellingWaitlistId === entry.id
                        ? '취소 처리 중...'
                        : `대기 ${entry.remainingQuantity}개 취소`}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {pendingOrderSheetGroups.length === 0 && storeWaitlists.length === 0 ? (
              <div className="py-14 flex flex-col items-center justify-center text-gray-400 gap-2 rounded-[24px] border border-dashed border-gray-200 bg-gray-50/60">
                <ShoppingBag size={34} strokeWidth={1.5} />
                <p className="text-sm font-bold">현재 담겨 있는 주문이 없습니다.</p>
              </div>
            ) : pendingOrderSheetGroups.length > 0 ? (
              pendingOrderSheetGroups.map((group) => {
                const {
                  key,
                  order,
                  sourceOrders,
                  displayDate,
                  orderSheetLabel,
                  visibleItemCount,
                  visibleTotalPrice,
                  requestDisplay,
                } = group;
                const statusMeta = STATUS_META[order.status] || STATUS_META.PENDING;
                const editable = isOrderEditable(order);
                const canOpenCombinedEdit =
                  !!pendingEditContext
                  && editable
                  && canCustomerEditOrder
                  && group.key === pendingEditContext.key;
                const visiblePickupLabel = acceptsPickupTime
                  ? requestDisplay.pickupLabel
                  : '';
                const visibleRequestMessage = acceptsRequestMessage
                  ? requestDisplay.requestMessage
                  : '';

                return (
                  <div
                    key={key}
                    className="rounded-[24px] border border-gray-100 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-black text-blue-600">
                          {orderSheetLabel}
                        </p>
                        <p className="text-[12px] font-bold text-gray-500 mt-0.5">
                          {displayDate}
                        </p>
                        {sourceOrders.length > 1 && (
                          <p className="text-[10px] font-bold text-blue-500 mt-1">
                            이 주문지의 주문 {sourceOrders.length}건 묶음
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-black ${statusMeta.className}`}
                        >
                          {statusMeta.label}
                        </span>
                        {canOpenCombinedEdit && (
                          <button
                            onClick={() =>
                              editable
                              && pendingEditContext
                              && setEditingOrderContext(pendingEditContext)
                            }
                            disabled={!editable}
                            className={`flex items-center gap-1 text-[11px] font-black px-2 py-1 rounded-lg transition-colors ${!editable
                                ? 'bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed'
                                : 'text-emerald-600 border border-emerald-100 bg-white hover:bg-emerald-50'
                              }`}
                          >
                            <Edit2 size={10} />
                            주문 수정
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {order.items.map((item, index) => {
                        return (
                          <OrderItemCard
                            key={buildOrderItemKey(key, item, index)}
                            groupKey={key}
                            item={item}
                            index={index}
                          />
                        );
                      })}
                    </div>

                    {(visiblePickupLabel || visibleRequestMessage) && (
                      <div className="mt-3 rounded-2xl bg-gray-50 border border-gray-100 px-3 py-2">
                        {visiblePickupLabel && (
                          <p className="text-[11px] font-bold text-gray-500">
                            방문 시간:{' '}
                            <span className="text-gray-700">{visiblePickupLabel}</span>
                          </p>
                        )}
                        {visibleRequestMessage && (
                          <p className="text-[11px] font-bold text-gray-500 whitespace-pre-line mt-1">
                            {visibleRequestMessage}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                      <div>
                        <span className="block text-[10px] font-bold text-gray-400">
                          주문지 상품 수
                        </span>
                        <span className="text-[11px] font-black text-gray-700">
                          {visibleItemCount}종
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="block text-[11px] font-bold text-gray-400">
                          주문지 전체 합계
                        </span>
                        <span className="font-black text-blue-700">
                          {visibleTotalPrice.toLocaleString()}원
                        </span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <button
                        onClick={() => handleFetchHistory(group)}
                        className="flex items-center gap-1.5 text-[11px] font-black text-gray-400 transition-colors hover:text-gray-900"
                      >
                        <History size={12} />
                        {expandedHistory[group.key] ? '이력 닫기' : '주문지 수정 이력'}
                      </button>
                    </div>

                    {expandedHistory[group.key] && (
                      <OrderEditTimeline logs={expandedHistory[group.key]} />
                    )}

                    {loadingHistory[group.key] && (
                      <div className="mt-3 rounded-2xl bg-gray-50 px-4 py-3 text-center text-[11px] font-bold text-gray-400">
                        이력 불러오는 중...
                      </div>
                    )}
                  </div>
                );
              })
            ) : null}
          </section>
        )}

        {activeTab === 'HISTORY' && (
          <section className="px-6 py-5 space-y-3">
            <div>
              <h3 className="text-sm font-black text-gray-900">구매한 상품</h3>
              <p className="text-[11px] font-bold text-gray-400 mt-1">
                수령완료 처리된 주문은 여기로 이동합니다.
              </p>
            </div>

            {completedOrderSheetGroups.length === 0 ? (
              <div className="py-14 flex flex-col items-center justify-center text-gray-400 gap-2 rounded-[24px] border border-dashed border-gray-200 bg-white">
                <Package size={34} strokeWidth={1.5} />
                <p className="text-sm font-bold">아직 구매 완료된 상품이 없습니다.</p>
              </div>
            ) : (
              completedOrderSheetGroups.map((group) => {
                const {
                  key,
                  order,
                  sourceOrders,
                  displayDate,
                  orderSheetLabel,
                  visibleItemCount,
                  visibleTotalPrice,
                } = group;
                const statusMeta = STATUS_META[order.status] || STATUS_META.COMPLETED;

                return (
                  <div
                    key={key}
                    className="rounded-[24px] border border-gray-100 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-black text-emerald-600">
                          {orderSheetLabel}
                        </p>
                        <p className="text-[12px] font-bold text-gray-500 mt-0.5">
                          {displayDate}
                        </p>
                        {sourceOrders.length > 1 && (
                          <p className="text-[10px] font-bold text-emerald-500 mt-1">
                            이 주문지의 주문 {sourceOrders.length}건 묶음
                          </p>
                        )}
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-black ${statusMeta.className}`}
                      >
                        {statusMeta.label}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {order.items.map((item, index) => {
                        return (
                          <OrderItemCard
                            key={buildOrderItemKey(key, item, index)}
                            groupKey={key}
                            item={item}
                            index={index}
                          />
                        );
                      })}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                      <div>
                        <span className="block text-[10px] font-bold text-gray-400">
                          구매 상품 수
                        </span>
                        <span className="text-[11px] font-black text-gray-700">
                          {visibleItemCount}종
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="block text-[11px] font-bold text-gray-400">
                          주문지 전체 합계
                        </span>
                        <span className="font-black text-emerald-700">
                          {visibleTotalPrice.toLocaleString()}원
                        </span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <button
                        onClick={() => handleFetchHistory(group)}
                        className="flex items-center gap-1.5 text-[11px] font-black text-gray-400 transition-colors hover:text-gray-900"
                      >
                        <History size={12} />
                        {expandedHistory[group.key] ? '이력 닫기' : '주문지 수정 이력'}
                      </button>
                    </div>

                    {expandedHistory[group.key] && (
                      <OrderEditTimeline logs={expandedHistory[group.key]} />
                    )}

                    {loadingHistory[group.key] && (
                      <div className="mt-3 rounded-2xl bg-gray-50 px-4 py-3 text-center text-[11px] font-bold text-gray-400">
                        이력 불러오는 중...
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </section>
        )}
      </main>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-lg z-[1001] bg-white border-t p-4 shadow-lg">
        <button
          onClick={onBack}
          className="w-full h-14 rounded-2xl bg-gray-900 text-white font-black flex items-center justify-center gap-2"
        >
          <ChevronLeft size={16} />
          상품 더 담으러 가기
        </button>
      </div>

      <StoreInfoModal
        isOpen={isStoreInfoOpen}
        onClose={() => setIsStoreInfoOpen(false)}
        store={currentStore}
      />
      <MyInfoModal
        isOpen={isMyInfoOpen}
        onClose={() => setIsMyInfoOpen(false)}
        profile={buyerProfile}
        onProfileUpdate={(updated) =>
          setBuyerProfile((prev) => ({ ...prev, ...updated }))
        }
      />
      {editingOrderContext && (
        <EditOrderBottomSheet
          isOpen={!!editingOrderContext}
          onClose={() => setEditingOrderContext(null)}
          order={editingOrderContext.order}
          orderLabel={
            activeOrderSheet?.label
              ? `${activeOrderSheet.label} 전체 주문`
              : '현재 주문 전체'
          }
          onLoadLatestOrder={reloadEditingOrderContext}
          onSave={handleEditOrderSave}
        />
      )}
      <Footer />
    </div>
  );
};

export default CartPage;
